import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Gauge } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PlaybackSpeedSelectorProps {
  speed: number
  onSpeedChange: (speed: number) => void
  disabled?: boolean
}

const SPEED_OPTIONS = [
  { value: 0.25, label: '0.25x' },
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1, label: '1x (Normal)' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 1.75, label: '1.75x' },
  { value: 2, label: '2x' },
]

export function PlaybackSpeedSelector({
  speed,
  onSpeedChange,
  disabled = false,
}: PlaybackSpeedSelectorProps) {
  const currentOption = SPEED_OPTIONS.find((opt) => opt.value === speed)
  const displaySpeed = currentOption ? currentOption.label.split(' ')[0] : `${speed}x`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            'gap-2 min-w-[5rem] font-mono',
            speed !== 1 && 'text-primary border-primary'
          )}
        >
          <Gauge className="h-4 w-4" />
          <span>{displaySpeed}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center">
        <DropdownMenuRadioGroup
          value={speed.toString()}
          onValueChange={(value) => onSpeedChange(parseFloat(value))}
        >
          {SPEED_OPTIONS.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value.toString()}
              className={cn(
                'font-mono cursor-pointer',
                option.value === 1 && 'font-semibold'
              )}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Compact version for inline use
export function PlaybackSpeedButtons({
  speed,
  onSpeedChange,
  disabled = false,
}: PlaybackSpeedSelectorProps) {
  const quickSpeeds = [0.5, 1, 1.5, 2]

  return (
    <div className="flex items-center gap-1">
      {quickSpeeds.map((s) => (
        <Button
          key={s}
          variant={speed === s ? 'default' : 'ghost'}
          size="sm"
          className="h-7 px-2 text-xs font-mono"
          disabled={disabled}
          onClick={() => onSpeedChange(s)}
        >
          {s}x
        </Button>
      ))}
    </div>
  )
}
